export interface EdgeAutoScrollConfig {
  edgeZonePx: number;
  minVelocityPxPerSec: number;
  maxVelocityPxPerSec: number;
}

export const DEFAULT_EDGE_AUTO_SCROLL_CONFIG: EdgeAutoScrollConfig = {
  edgeZonePx: 32,
  minVelocityPxPerSec: 120,
  maxVelocityPxPerSec: 1500,
};

type ScrollDirection = -1 | 0 | 1;

export class EdgeAutoScroller {
  private rafId: number | null = null;
  private lastTickTs = 0;
  private direction: ScrollDirection = 0;
  private intensity = 0;

  constructor(
    private readonly viewport: HTMLElement,
    private readonly config: EdgeAutoScrollConfig = DEFAULT_EDGE_AUTO_SCROLL_CONFIG
  ) {}

  public update(viewportPointerY: number): void {
    const height = this.viewport.clientHeight;
    const { edgeZonePx } = this.config;

    if (viewportPointerY < edgeZonePx) {
      this.direction = -1;
      this.intensity = this.computeIntensity(edgeZonePx - viewportPointerY, edgeZonePx);
      this.ensureLoop();
      return;
    }

    if (viewportPointerY > height - edgeZonePx) {
      this.direction = 1;
      this.intensity = this.computeIntensity(viewportPointerY - (height - edgeZonePx), edgeZonePx);
      this.ensureLoop();
      return;
    }

    this.stop();
  }

  public stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.direction = 0;
    this.intensity = 0;
    this.lastTickTs = 0;
  }

  public isRunning(): boolean {
    return this.rafId !== null;
  }

  private ensureLoop(): void {
    if (this.rafId !== null) return;
    this.lastTickTs = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  private tick = (ts: number): void => {
    if (this.rafId === null) return;

    if (this.lastTickTs === 0) {
      this.lastTickTs = ts;
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    const dt = (ts - this.lastTickTs) / 1000;
    this.lastTickTs = ts;

    const { minVelocityPxPerSec: vMin, maxVelocityPxPerSec: vMax } = this.config;
    const v = vMin + (vMax - vMin) * this.intensity;
    this.viewport.scrollBy(0, this.direction * v * dt);

    this.rafId = requestAnimationFrame(this.tick);
  };

  private computeIntensity(distIntoZone: number, zonePx: number): number {
    const t = Math.min(Math.max(distIntoZone / zonePx, 0), 1);
    return t * t;
  }
}
